import { CreateQuoteDialog } from "@/components/quotes/CreateQuoteDialog";
import {
  CustomerDeduplicationCard,
  CustomerReviewSummary,
  PhoneValidationCard,
} from "@/components/review/SubmissionReviewCards";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Customer } from "@/core";
import { formatDateTime } from "@/utils/submission-review";
import { FileText, Mail, Phone, UserRound } from "lucide-react";
import { useState } from "react";

interface CustomerDetailsSheetProps {
  customer: Customer;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function CustomerDetailsSheet({
  customer,
  onOpenChange,
  open,
}: Readonly<CustomerDetailsSheetProps>) {
  const [isPhoneHistoryOpen, setIsPhoneHistoryOpen] = useState(false);
  const [isDeduplicationHistoryOpen, setIsDeduplicationHistoryOpen] =
    useState(false);
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false);
  const shouldStretchReviewCards =
    isPhoneHistoryOpen !== isDeduplicationHistoryOpen;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto p-4 sm:max-w-3xl sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle>
            {customer.firstName} {customer.lastName}
          </SheetTitle>
          <SheetDescription>
            Customer profile, duplicate submission review, and phone validation
            checks.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="rounded-lg border p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Customer Overview</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Basic contact details and review signals at a glance.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCreateQuoteOpen(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Create Quote
              </Button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="flex items-start gap-3 rounded-md bg-muted/30 p-3">
                <UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Customer ID
                  </p>
                  <p className="text-sm font-medium">{customer.id}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md bg-muted/30 p-3">
                <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Email
                  </p>
                  <p className="text-sm font-medium">{customer.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md bg-muted/30 p-3">
                <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Phone
                  </p>
                  <p className="text-sm font-medium">{customer.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md bg-muted/30 p-3">
                <UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Updated
                  </p>
                  <p className="text-sm font-medium">
                    {formatDateTime(customer.updatedAt)}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <CustomerReviewSummary customer={customer} />
            </div>
          </div>

          <div
            className={`grid gap-4 lg:grid-cols-2 ${
              shouldStretchReviewCards ? "items-stretch" : "items-start"
            }`}
          >
            <PhoneValidationCard
              result={customer.latestPhoneValidation}
              history={customer.phoneValidationHistory}
              title="Phone Validation"
              onHistoryOpenChange={setIsPhoneHistoryOpen}
            />
            <CustomerDeduplicationCard
              events={customer.dealDeduplicationEvents}
              onHistoryOpenChange={setIsDeduplicationHistoryOpen}
            />
          </div>
        </div>

        <Separator className="my-6" />

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>

      <CreateQuoteDialog
        open={isCreateQuoteOpen}
        onOpenChange={setIsCreateQuoteOpen}
        customer={customer}
      />
    </Sheet>
  );
}
